import React, { useEffect, useState } from "react";
import { Settings, User, MapPin, Bell, ArrowLeft } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";

export default function CustomerSettings({ session, profile, onBack, onProfileUpdated }) {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [defaultAddress, setDefaultAddress] = useState("");
  const [defaultSuburb, setDefaultSuburb] = useState("");
  const [defaultPostcode, setDefaultPostcode] = useState("");
  const [defaultState, setDefaultState] = useState("");
  const [notifyQuoteReceived, setNotifyQuoteReceived] = useState(true);
  const [notifyMessageReceived, setNotifyMessageReceived] = useState(true);
  const [acceptMarketing, setAcceptMarketing] = useState(false);

  const email = session?.user?.email || "";

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name || "");
    setPhone(profile.phone || "");
    setDefaultAddress(profile.default_address || "");
    setDefaultSuburb(profile.default_suburb || "");
    setDefaultPostcode(profile.default_postcode || "");
    setDefaultState(profile.default_state || "");
    setNotifyQuoteReceived(profile.notify_quote_received !== false);
    setNotifyMessageReceived(profile.notify_message_received !== false);
    setAcceptMarketing(profile.accept_marketing || false);
  }, [profile]);

  async function handleSave() {
    if (!session?.user?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName.trim(),
          phone: phone.trim(),
          default_address: defaultAddress.trim(),
          default_suburb: defaultSuburb.trim(),
          default_postcode: defaultPostcode.trim(),
          default_state: defaultState.trim(),
          notify_quote_received: notifyQuoteReceived,
          notify_message_received: notifyMessageReceived,
          accept_marketing: acceptMarketing,
        })
        .eq("id", session.user.id);

      if (error) throw error;
      showToast("Settings saved.", "success");
      onProfileUpdated?.();
    } catch (err) {
      showToast(err.message || "Failed to save settings.", "error");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid var(--border, #e5e7eb)",
    fontSize: "0.9rem",
    background: "var(--surface, #fff)",
    color: "var(--text, #1C1C1E)",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: "6px",
    color: "var(--text, #1C1C1E)",
  };

  const sectionStyle = {
    marginBottom: "28px",
  };

  const toggleRow = (label, value, onChange) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 0",
        borderBottom: "1px solid var(--border, #e5e7eb)",
      }}
    >
      <span style={{ fontSize: "0.9rem", color: "var(--text, #1C1C1E)" }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
          width: "44px",
          height: "24px",
          borderRadius: "12px",
          border: "none",
          background: value ? "var(--primary)" : "var(--border, #d1d5db)",
          position: "relative",
          cursor: "pointer",
          transition: "background 0.2s",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#fff",
            position: "absolute",
            top: "2px",
            left: value ? "22px" : "2px",
            transition: "left 0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </div>
  );

  return (
    <div className="page customer-shell">
      <button
        type="button"
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "none",
          border: "none",
          color: "var(--text-muted, #6b7280)",
          fontSize: "0.9rem",
          fontWeight: 600,
          cursor: "pointer",
          padding: "0",
          marginBottom: "16px",
        }}
      >
        <ArrowLeft size={16} /> Back to dashboard
      </button>

      <h1 style={{ margin: "0 0 24px", fontSize: "1.5rem", fontWeight: 800, color: "var(--text, #1C1C1E)" }}>
        <Settings size={22} style={{ verticalAlign: "middle", marginRight: "8px" }} />
        Settings
      </h1>

      <Card>
        {/* Personal Details */}
        <div style={sectionStyle}>
          <h3 style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 16px" }}>
            <User size={18} /> Personal details
          </h3>
          <p style={{ color: "var(--text-muted, #6b7280)", fontSize: "0.85rem", margin: "0 0 16px" }}>
            These details pre-fill your quote request forms so you don't have to type them every time.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Full name</label>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={inputStyle} placeholder="Your full name" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={email} disabled style={{ ...inputStyle, opacity: 0.6, cursor: "not-allowed" }} />
              <p style={{ color: "var(--text-muted, #9ca3af)", fontSize: "0.75rem", margin: "4px 0 0" }}>
                Managed by your login — can't be changed here.
              </p>
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} placeholder="04xx xxx xxx" type="tel" />
            </div>
          </div>
        </div>

        {/* Default Address */}
        <div style={sectionStyle}>
          <h3 style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 16px" }}>
            <MapPin size={18} /> Default address
          </h3>
          <p style={{ color: "var(--text-muted, #6b7280)", fontSize: "0.85rem", margin: "0 0 16px" }}>
            Pre-fills the address on new quote requests. You can always change it per job.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Street address</label>
              <input value={defaultAddress} onChange={(e) => setDefaultAddress(e.target.value)} style={inputStyle} placeholder="123 Example Street" />
            </div>
            <div>
              <label style={labelStyle}>Suburb</label>
              <input value={defaultSuburb} onChange={(e) => setDefaultSuburb(e.target.value)} style={inputStyle} placeholder="Suburb" />
            </div>
            <div>
              <label style={labelStyle}>Postcode</label>
              <input value={defaultPostcode} onChange={(e) => setDefaultPostcode(e.target.value)} style={inputStyle} placeholder="3000" maxLength={4} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <select value={defaultState} onChange={(e) => setDefaultState(e.target.value)} style={inputStyle}>
                <option value="">Select</option>
                <option value="VIC">VIC</option>
                <option value="NSW">NSW</option>
                <option value="QLD">QLD</option>
                <option value="SA">SA</option>
                <option value="WA">WA</option>
                <option value="TAS">TAS</option>
                <option value="NT">NT</option>
                <option value="ACT">ACT</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div style={sectionStyle}>
          <h3 style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 16px" }}>
            <Bell size={18} /> Notifications
          </h3>
          {toggleRow("New quote received", notifyQuoteReceived, setNotifyQuoteReceived)}
          {toggleRow("New message from tradie", notifyMessageReceived, setNotifyMessageReceived)}
          {toggleRow("Marketing & tips emails", acceptMarketing, setAcceptMarketing)}
        </div>

        {/* Save */}
        <div className="button-row">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save settings"}
          </Button>
          <Button secondary onClick={onBack}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}
